import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import * as XLSX from 'xlsx';
import { useExcelDocument } from 'hooks/useFileType/useExcelDocument';
import { logError } from 'setup/logger/logError';
import {
  sanitizeExcelThumbnailCellValue,
  truncateExcelThumbnailCellValue,
} from 'shared/utils/excelThumbnailSanitization';
import {
  ThumbnailErrorFallback,
  ThumbnailLoadingOverlay,
} from './common';
import {
  getThumbnailContentStyle,
  THUMBNAIL_CONTAINER_SX,
} from './styles';
import { ThumbnailProps } from './types';

const MAX_CELLS_TO_RENDER = 50; // Max cells per dimension (50x50 = 2500 cells)

export const ExcelThumbnail: React.FC<ThumbnailProps> = ({
  src,
  thumbnailSize,
  onClick,
  fileType,
  fileExtension,
}): ReactElement => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { loading, error, workbook } = useExcelDocument(src);
  const canvasStyle = useMemo(
    () => getThumbnailContentStyle(loading),
    [loading]
  );

  useEffect(() => {
    if (!workbook || !canvasRef.current) return;

    try {
      // Get first worksheet
      const worksheetName = workbook.SheetNames[0];
      if (!worksheetName) {
        return;
      }

      const worksheet = workbook.Sheets[worksheetName];
      const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
      });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }

      // Set canvas size
      canvas.width = thumbnailSize;
      canvas.height = thumbnailSize;

      // Clear canvas
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);

      // Render grid and content with limits
      const cellHeight = 18;
      const cellWidth = 80;
      const maxRows = Math.min(jsonData.length, 10, MAX_CELLS_TO_RENDER);
      const maxCols = Math.min(
        Math.max(...jsonData.map((row) => row.length)),
        5,
        MAX_CELLS_TO_RENDER
      );

      // Draw grid
      context.strokeStyle = '#d0d0d0';
      context.lineWidth = 0.5;

      // Vertical lines
      for (let i = 0; i <= maxCols; i++) {
        context.beginPath();
        context.moveTo(i * cellWidth, 0);
        context.lineTo(i * cellWidth, maxRows * cellHeight);
        context.stroke();
      }

      // Horizontal lines
      for (let i = 0; i <= maxRows; i++) {
        context.beginPath();
        context.moveTo(0, i * cellHeight);
        context.lineTo(maxCols * cellWidth, i * cellHeight);
        context.stroke();
      }

      // Draw cell content
      context.fillStyle = '#000000';
      context.font = '10px Arial';
      context.textBaseline = 'middle';

      for (let rowIndex = 0; rowIndex < maxRows; rowIndex++) {
        const row = jsonData[rowIndex] || [];
        for (let colIndex = 0; colIndex < maxCols; colIndex++) {
          const cellValue = row[colIndex];
          if (cellValue !== null && cellValue !== undefined) {
            const sanitizedValue =
              sanitizeExcelThumbnailCellValue(cellValue);
            const displayValue =
              truncateExcelThumbnailCellValue(sanitizedValue);

            const x = colIndex * cellWidth + 4;
            const y = rowIndex * cellHeight + cellHeight / 2;

            // Apply bold for first row (header)
            if (rowIndex === 0) {
              context.font = 'bold 10px Arial';
            } else {
              context.font = '10px Arial';
            }

            context.fillText(displayValue, x, y);
          }
        }
      }

      // Add fade effect at bottom
      const gradient = context.createLinearGradient(
        0,
        canvas.height * 0.7,
        0,
        canvas.height
      );
      gradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      gradient.addColorStop(1, 'rgba(242, 242, 242, 1)');
      context.fillStyle = gradient;
      context.fillRect(
        0,
        canvas.height * 0.7,
        canvas.width,
        canvas.height
      );
    } catch (err) {
      // Error handling is managed by useExcelDocument hook
      logError('Error rendering Excel thumbnail:', err);
    }
  }, [workbook, thumbnailSize]);

  if (error) {
    return (
      <ThumbnailErrorFallback
        onClick={onClick}
        fileType={fileType}
        fileExtension={fileExtension}
      />
    );
  }

  return (
    <Box onClick={onClick} sx={THUMBNAIL_CONTAINER_SX}>
      {loading && <ThumbnailLoadingOverlay />}
      <canvas
        ref={canvasRef}
        style={canvasStyle}
      />
    </Box>
  );
};
